/**
 * TrackVisionAnnotationPage.jsx — Ground Truth Annotation Tool (Athletics)
 * =========================================================================
 * Frame-by-frame video annotation for sprint/gait biomechanics validation.
 * Modeled after SwimAnnotationsPage.jsx — same UX patterns, athletics domain.
 *
 * Route: /track-vision/sessions/:sessionId/annotate
 *
 * Keyboard shortcuts:
 *   Space       → Play / Pause
 *   Enter       → Add annotation (current type)
 *   ← / →       → -1 / +1 frame (Shift: ±10)
 *   [ / ]       → Speed down / up
 *   F           → Foot Strike
 *   T           → Toe Off
 *   G           → Ground Contact
 *   V           → Flight Phase
 *   P           → Sprint Phase
 *   K           → Knee Angle
 *   H           → Hip Angle
 *   1-5         → Set sprint phase label
 *   L / R       → Set side left / right
 *   Cmd+Z       → Undo last annotation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSportsLang } from '../contexts/SportsLangContext';
import {
  ArrowLeft, Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  Save, Trash2, Plus, Tag, Zap, Activity, Target, TrendingUp,
  CheckCircle2, AlertTriangle, Download, Upload, BarChart3,
} from 'lucide-react';
import { getSession } from '../services/trackVisionService';
import {
  getAnnotations, createAnnotation, deleteAnnotation,
  bulkCreateAnnotations, exportAnnotationsJSON, getAccuracyReport,
} from '../services/trackVisionAnnotationService';

/* ═══════════════════════════════════════════════════════════════════
   Design Tokens (VUKZ B&W minimal)
   ═══════════════════════════════════════════════════════════════════ */
const T = {
  bg: '#fafafa', card: '#ffffff', text: '#111111',
  textSoft: '#6b7280', textMute: '#9ca3af', border: '#e5e7eb',
  accent: '#111111', accentSoft: '#f3f4f6',
  success: '#16a34a', successBg: '#f0fdf4',
  warning: '#f59e0b', warningBg: '#fffbeb',
  error: '#dc2626', errorBg: '#fef2f2',
  info: '#2563eb', infoBg: '#eff6ff',
};

/* Event type configuration — Athletics domain */
const EVENT_TYPES = [
  { key: 'foot_strike',    label: 'Foot Strike',    color: '#dc2626', shortcut: 'F', icon: Zap },
  { key: 'toe_off',        label: 'Toe Off',        color: '#2563eb', shortcut: 'T', icon: TrendingUp },
  { key: 'ground_contact', label: 'Ground Contact', color: '#d97706', shortcut: 'G', icon: Target },
  { key: 'flight_phase',   label: 'Flight Phase',   color: '#7c3aed', shortcut: 'V', icon: Activity },
  { key: 'sprint_phase',   label: 'Sprint Phase',   color: '#059669', shortcut: 'P', icon: Activity },
  { key: 'knee_angle',     label: 'Knee Angle',     color: '#0891b2', shortcut: 'K', icon: Target },
  { key: 'hip_angle',      label: 'Hip Angle',      color: '#6366f1', shortcut: 'H', icon: Target },
  { key: 'trunk_angle',    label: 'Trunk Angle',    color: '#374151', shortcut: null, icon: Target },
  { key: 'stride_marker',  label: 'Stride Marker',  color: '#111111', shortcut: null, icon: CheckCircle2 },
  { key: 'technical_note', label: 'Technical Note', color: '#9ca3af', shortcut: null, icon: Tag },
];

const EVENT_MAP = Object.fromEntries(EVENT_TYPES.map(e => [e.key, e]));

const PHASE_LABELS = [
  { key: 'block_start', pt: 'Saída', en: 'Start', num: '1' },
  { key: 'drive', pt: 'Aceleração', en: 'Drive', num: '2' },
  { key: 'transition', pt: 'Transição', en: 'Transition', num: '3' },
  { key: 'max_velocity', pt: 'Vel. Máxima', en: 'Max Vel.', num: '4' },
  { key: 'deceleration', pt: 'Desaceleração', en: 'Deceleration', num: '5' },
];

const LABELS_I18N = {
  pt: {
    loading: (id) => `Carregando sessão #${id}...`,
    notFound: (id) => `Sessão #${id} não encontrada`,
    backToSessions: 'Voltar às Sessões',
    videoUnavailable: 'Vídeo não disponível (sem URL)',
    videoError: 'Falha ao carregar vídeo',
    savedMsg: '✓ Salvo',
    saveError: 'Erro ao salvar',
    kbHint: 'Tipo de evento · Enter = adicionar · Espaço = play/pause · ↔ = frame · [/] = velocidade · Cmd+Z = desfazer',
    sideLabel: 'Lado:', left: 'Esq (L)', right: 'Dir (R)',
    phaseLabel: 'Fase:',
    manualAnnotations: 'Anotações manuais', aiPhases: 'Fases AI',
    summaryTitle: (n) => `Resumo (${n} anotações)`,
    eventsTitle: 'Eventos marcados',
    noAnnotations: 'Nenhuma anotação ainda.',
    noAnnotationsHint: 'Use os atalhos ou Enter para marcar eventos.',
    sideAbbr: (side) => side === 'left' ? 'E' : 'D',
  },
  en: {
    loading: (id) => `Loading session #${id}...`,
    notFound: (id) => `Session #${id} not found`,
    backToSessions: 'Back to Sessions',
    videoUnavailable: 'Video unavailable (no URL)',
    videoError: 'Failed to load video',
    savedMsg: '✓ Saved',
    saveError: 'Save error',
    kbHint: 'Event type · Enter = add · Space = play/pause · ↔ = frame · [/] = speed · Cmd+Z = undo',
    sideLabel: 'Side:', left: 'Left (L)', right: 'Right (R)',
    phaseLabel: 'Phase:',
    manualAnnotations: 'Manual annotations', aiPhases: 'AI Phases',
    summaryTitle: (n) => `Summary (${n} annotations)`,
    eventsTitle: 'Marked events',
    noAnnotations: 'No annotations yet.',
    noAnnotationsHint: 'Use shortcuts or Enter to mark events.',
    sideAbbr: (side) => side === 'left' ? 'L' : 'R',
  },
};

/* ═══════════════════════════════════════════════════════════════════
   Annotation Page
   ═══════════════════════════════════════════════════════════════════ */
export default function TrackVisionAnnotationPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;

  // State
  const [session, setSession] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeType, setActiveType] = useState('foot_strike');
  const [activeSide, setActiveSide] = useState(null);
  const [activePhaseLabel, setActivePhaseLabel] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [savedMsg, setSavedMsg] = useState('');
  const [videoError, setVideoError] = useState(null);
  const [accuracy, setAccuracy] = useState(null);

  const fps = session?.video_fps || 30;
  const videoRef = useRef(null);
  const RATES = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2];

  // Fetch session + annotations
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const sRes = await getSession(sessionId);
        setSession(sRes.data || sRes);
        const aRes = await getAnnotations(sessionId);
        setAnnotations((aRes.data || []).sort((a, b) => (a.timestamp_ms || 0) - (b.timestamp_ms || 0)));
      } catch (err) {
        console.error('[Annotation] Load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId]);

  const videoUrl = session?.video_blob_url || session?.overlay_blob_url;

  // Add annotation at current time
  const addAnnotation = useCallback(() => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    const frameNum = Math.round(t * fps);
    const timestampMs = Math.round(t * 1000);

    const newAnn = {
      annotation_type: activeType,
      frame_index: frameNum,
      timestamp_ms: timestampMs,
      side: activeSide,
      label: activeType === 'sprint_phase' ? activePhaseLabel : null,
      confidence: 1.0,
      notes: null,
    };

    setAnnotations(prev => [...prev, newAnn].sort((a, b) => (a.timestamp_ms || 0) - (b.timestamp_ms || 0)));
    setDirty(true);
  }, [activeType, activeSide, activePhaseLabel, fps]);

  // Remove annotation
  const removeAnnotation = (idx) => {
    setAnnotations(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  // Save to backend (bulk)
  const handleSave = async () => {
    setSaving(true);
    try {
      const unsaved = annotations.filter(a => !a.id);
      if (unsaved.length > 0) {
        await bulkCreateAnnotations(sessionId, unsaved);
      }
      const aRes = await getAnnotations(sessionId);
      setAnnotations((aRes.data || []).sort((a, b) => (a.timestamp_ms || 0) - (b.timestamp_ms || 0)));
      setDirty(false);
      setSavedMsg(L.savedMsg);
      setTimeout(() => setSavedMsg(''), 2000);
    } catch (err) {
      console.error('[Annotation] Save error:', err);
      alert(`${L.saveError}: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Video controls
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  }, []);

  const seekFrame = useCallback((delta) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += delta / fps;
  }, [fps]);

  const seekTo = useCallback((t) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = t;
  }, []);

  const speedDown = useCallback(() => {
    setPlaybackRate(prev => {
      const idx = RATES.indexOf(prev);
      const next = RATES[Math.max(0, idx - 1)];
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const speedUp = useCallback(() => {
    setPlaybackRate(prev => {
      const idx = RATES.indexOf(prev);
      const next = RATES[Math.min(RATES.length - 1, idx + 1)];
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Event type shortcuts
      const type = EVENT_TYPES.find(t => t.shortcut && t.shortcut.toLowerCase() === e.key.toLowerCase());
      if (type && !e.metaKey && !e.ctrlKey) {
        setActiveType(type.key);
        return;
      }

      // Phase shortcuts (1-5)
      const phaseIdx = parseInt(e.key) - 1;
      if (phaseIdx >= 0 && phaseIdx < PHASE_LABELS.length) {
        setActiveType('sprint_phase');
        setActivePhaseLabel(PHASE_LABELS[phaseIdx].key);
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'Enter':
          e.preventDefault();
          addAnnotation();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekFrame(e.shiftKey ? -10 : -1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekFrame(e.shiftKey ? 10 : 1);
          break;
        case '[':
          e.preventDefault();
          speedDown();
          break;
        case ']':
          e.preventDefault();
          speedUp();
          break;
        case 'l': case 'L':
          if (!e.metaKey && !e.ctrlKey) setActiveSide(s => s === 'left' ? null : 'left');
          break;
        case 'r': case 'R':
          if (!e.metaKey && !e.ctrlKey) setActiveSide(s => s === 'right' ? null : 'right');
          break;
        case 'z': case 'Z':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            setAnnotations(prev => prev.slice(0, -1));
            setDirty(true);
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePlay, addAnnotation, seekFrame, speedDown, speedUp]);

  // Export annotations as JSON
  const handleExport = () => {
    const blob = new Blob([JSON.stringify({
      session_id: parseInt(sessionId),
      event_type: session?.event_type,
      fps,
      exported_at: new Date().toISOString(),
      annotations,
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trackvision_annotations_${sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import annotations from JSON
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (ev) => {
      const file = ev.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          const imported = data.annotations || data;
          if (Array.isArray(imported)) {
            setAnnotations(prev => [...prev, ...imported].sort((a, b) => (a.timestamp_ms || 0) - (b.timestamp_ms || 0)));
            setDirty(true);
          }
        } catch (err) {
          alert(lang === 'en' ? 'Error importing JSON' : 'Erro ao importar JSON');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Load accuracy report
  const loadAccuracy = async () => {
    try {
      const res = await getAccuracyReport(sessionId);
      setAccuracy(res.data || res);
    } catch (err) {
      console.error('[Annotation] Accuracy error:', err);
    }
  };

  // Count by type
  const counts = annotations.reduce((acc, a) => {
    acc[a.annotation_type] = (acc[a.annotation_type] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: T.textSoft }}>{L.loading(sessionId)}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: T.warning }} />
          <p className="font-bold" style={{ color: T.text }}>{L.notFound(sessionId)}</p>
          <Link to="/track-vision/sessions" className="text-sm underline" style={{ color: T.info }}>
            {L.backToSessions}
          </Link>
        </div>
      </div>
    );
  }

  const fmtTime = (ms) => {
    const s = (ms || 0) / 1000;
    const min = Math.floor(s / 60);
    const sec = (s % 60).toFixed(2);
    return `${min}:${sec.padStart(5, '0')}`;
  };

  const fmtTimeSec = (s) => {
    const min = Math.floor(s / 60);
    const sec = (s % 60).toFixed(2);
    return `${min}:${sec.padStart(5, '0')}`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      {/* Header */}
      <div className="sticky top-0 z-40 border-b" style={{ backgroundColor: T.card, borderColor: T.border }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/track-vision/sessions/${sessionId}`)} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold" style={{ color: T.text }}>
              Annotation Tool — Sessão #{sessionId}
            </h1>
            <p className="text-xs" style={{ color: T.textSoft }}>
              {session.event_type || 'sprint'} • {session.video_filename || 'vídeo'} •{' '}
              {L.sideLabel} <strong>{activeSide === 'left' ? L.left : activeSide === 'right' ? L.right : '—'}</strong>
              {activePhaseLabel && activeType === 'sprint_phase' && (
                <> • {L.phaseLabel} <strong>{PHASE_LABELS.find(p => p.key === activePhaseLabel)?.[lang === 'en' ? 'en' : 'pt'] || activePhaseLabel}</strong></>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {savedMsg && (
              <span className="text-xs font-bold px-2 py-1 rounded" style={{ color: T.success, backgroundColor: T.successBg }}>
                {savedMsg}
              </span>
            )}
            {dirty && (
              <span className="text-xs px-2 py-1 rounded" style={{ color: T.warning, backgroundColor: T.warningBg }}>
                Não salvo
              </span>
            )}
            <button onClick={loadAccuracy}
              className="p-2 rounded-lg hover:bg-gray-100" title="Accuracy Report">
              <BarChart3 size={16} style={{ color: T.textSoft }} />
            </button>
            <button onClick={handleImport}
              className="p-2 rounded-lg hover:bg-gray-100" title="Importar JSON">
              <Upload size={16} style={{ color: T.textSoft }} />
            </button>
            <button onClick={handleExport}
              className="p-2 rounded-lg hover:bg-gray-100" title="Exportar JSON">
              <Download size={16} style={{ color: T.textSoft }} />
            </button>
            <button onClick={handleSave} disabled={saving || !dirty}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40"
              style={{ backgroundColor: T.accent }}>
              <Save size={14} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Video + Controls */}
        <div className="lg:col-span-2 space-y-3">
          {/* Video */}
          <div className="rounded-xl overflow-hidden bg-black relative" style={{ aspectRatio: '16/9' }}>
            {videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                  onLoadedMetadata={() => {
                    setDuration(videoRef.current?.duration || 0);
                    videoRef.current.playbackRate = playbackRate;
                  }}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onError={() => setVideoError(L.videoError)}
                />
                {videoError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center p-4">
                      <p className="text-red-400 text-sm font-bold">❌ {videoError}</p>
                      <p className="text-white/40 text-xs mt-2 break-all max-w-md">{videoUrl?.substring(0, 120)}...</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-white/50 text-sm">{L.videoUnavailable}</p>
              </div>
            )}
          </div>

          {/* Timeline with markers */}
          <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <div className="relative h-8 bg-gray-100 rounded-lg mb-2 cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                seekTo(pct * duration);
              }}>
              {/* Playhead */}
              <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                style={{ left: `${(currentTime / Math.max(duration, 1)) * 100}%` }} />
              {/* Annotation markers */}
              {annotations.map((ann, i) => {
                const cfg = EVENT_MAP[ann.annotation_type] || { color: '#999' };
                const timeS = (ann.timestamp_ms || 0) / 1000;
                return (
                  <div key={i} className="absolute top-1 w-1.5 h-6 rounded-full opacity-80 hover:opacity-100 cursor-pointer"
                    style={{
                      left: `${(timeS / Math.max(duration, 1)) * 100}%`,
                      backgroundColor: cfg.color,
                    }}
                    title={`${cfg.label || ann.annotation_type} @ ${fmtTime(ann.timestamp_ms)}`}
                    onClick={(e) => { e.stopPropagation(); seekTo(timeS); }}
                  />
                );
              })}
            </div>

            {/* Controls row */}
            <div className="flex items-center gap-2">
              <button onClick={() => seekFrame(-10)} className="p-1.5 rounded hover:bg-gray-100" title="← 10 frames (Shift+←)">
                <SkipBack size={14} />
              </button>
              <button onClick={() => seekFrame(-1)} className="p-1.5 rounded hover:bg-gray-100" title="← 1 frame (←)">
                <ChevronLeft size={14} />
              </button>
              <button onClick={togglePlay}
                className="p-2 rounded-lg text-white" style={{ backgroundColor: T.accent }}>
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button onClick={() => seekFrame(1)} className="p-1.5 rounded hover:bg-gray-100" title="→ 1 frame (→)">
                <ChevronRight size={14} />
              </button>
              <button onClick={() => seekFrame(10)} className="p-1.5 rounded hover:bg-gray-100" title="→ 10 frames (Shift+→)">
                <SkipForward size={14} />
              </button>

              <div className="flex-1 text-center">
                <span className="text-xs font-mono font-bold" style={{ color: T.text }}>
                  {fmtTimeSec(currentTime)} / {fmtTimeSec(duration)}
                </span>
                <span className="text-xs ml-2" style={{ color: T.textMute }}>
                  f{Math.round(currentTime * fps)}
                </span>
              </div>

              {/* Speed controls */}
              <div className="flex items-center gap-0.5">
                <button onClick={speedDown} title="Slower ["
                  className="px-1.5 py-1 rounded-l text-xs font-bold border"
                  style={{ borderColor: T.border, color: T.text }}>−</button>
                <button onClick={() => {
                  setPlaybackRate(1);
                  if (videoRef.current) videoRef.current.playbackRate = 1;
                }}
                  className="px-2 py-1 text-xs font-bold border-y"
                  style={{ borderColor: T.border, color: playbackRate < 1 ? '#ef4444' : T.text, backgroundColor: playbackRate < 1 ? '#fef2f2' : 'transparent' }}>
                  {playbackRate}×
                </button>
                <button onClick={speedUp} title="Faster ]"
                  className="px-1.5 py-1 rounded-r text-xs font-bold border"
                  style={{ borderColor: T.border, color: T.text }}>+</button>
              </div>

              {/* Add annotation button */}
              <button onClick={addAnnotation}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold text-white"
                style={{ backgroundColor: EVENT_MAP[activeType]?.color || T.accent }}>
                <Plus size={14} />
                {EVENT_MAP[activeType]?.label || activeType}
              </button>
            </div>
          </div>

          {/* Event type selector */}
          <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <p className="text-xs font-bold mb-2" style={{ color: T.textSoft }}>
              {L.kbHint}
            </p>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map(evt => (
                <button key={evt.key}
                  onClick={() => setActiveType(evt.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
                  style={{
                    borderColor: activeType === evt.key ? evt.color : T.border,
                    backgroundColor: activeType === evt.key ? evt.color + '15' : T.card,
                    color: activeType === evt.key ? evt.color : T.textSoft,
                  }}>
                  <evt.icon size={12} />
                  {evt.label}
                  {evt.shortcut && <span className="text-[10px] opacity-60">({evt.shortcut})</span>}
                  {counts[evt.key] ? (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] text-white"
                      style={{ backgroundColor: evt.color }}>
                      {counts[evt.key]}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            {/* Phase + Side controls */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t" style={{ borderColor: T.border }}>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: T.textMute }}>{L.sideLabel}</span>
                {['left', 'right'].map(side => (
                  <button key={side}
                    onClick={() => setActiveSide(s => s === side ? null : side)}
                    className="text-xs px-2 py-0.5 rounded border font-bold transition-all"
                    style={{
                      borderColor: activeSide === side ? T.accent : T.border,
                      backgroundColor: activeSide === side ? T.accent : 'transparent',
                      color: activeSide === side ? 'white' : T.textSoft,
                    }}>
                    {side === 'left' ? L.left : L.right}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: T.textMute }}>{L.phaseLabel}</span>
                {PHASE_LABELS.map(p => (
                  <button key={p.key}
                    onClick={() => { setActiveType('sprint_phase'); setActivePhaseLabel(p.key); }}
                    className="text-[10px] px-2 py-0.5 rounded border font-bold transition-all"
                    style={{
                      borderColor: activePhaseLabel === p.key && activeType === 'sprint_phase' ? '#059669' : T.border,
                      backgroundColor: activePhaseLabel === p.key && activeType === 'sprint_phase' ? '#059669' : 'transparent',
                      color: activePhaseLabel === p.key && activeType === 'sprint_phase' ? 'white' : T.textSoft,
                    }}>
                    {p.num} {lang === 'en' ? p.en : p.pt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Accuracy report */}
          {accuracy && (
            <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
              <h3 className="text-sm font-bold mb-2" style={{ color: T.text }}>
                <BarChart3 size={14} className="inline mr-1" /> AI vs Manual — Accuracy Report
              </h3>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold" style={{ color: T.text }}>
                    {accuracy.manual_annotation_count ?? '—'}
                  </div>
                  <div style={{ color: T.textMute }}>{L.manualAnnotations}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold" style={{ color: T.text }}>
                    {accuracy.ai_phase_count ?? '—'}
                  </div>
                  <div style={{ color: T.textMute }}>{L.aiPhases}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold" style={{ color: accuracy.breakdown?.phase_accuracy_pct >= 80 ? T.success : T.warning }}>
                    {accuracy.breakdown?.phase_accuracy_pct != null ? `${accuracy.breakdown.phase_accuracy_pct}%` : '—'}
                  </div>
                  <div style={{ color: T.textMute }}>{lang === 'en' ? 'Phase Match' : 'Phase Match'}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Annotations list */}
        <div className="space-y-3">
          {/* Summary */}
          <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <h3 className="text-sm font-bold mb-2" style={{ color: T.text }}>
              {L.summaryTitle(annotations.length)}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {EVENT_TYPES.filter(e => counts[e.key]).map(evt => (
                <div key={evt.key} className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: evt.color }} />
                  <span style={{ color: T.textSoft }}>{evt.label}:</span>
                  <span className="font-bold" style={{ color: T.text }}>{counts[evt.key]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Annotations list */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: T.border }}>
              <h3 className="text-sm font-bold" style={{ color: T.text }}>{L.eventsTitle}</h3>
              <span className="text-xs" style={{ color: T.textMute }}>{annotations.length}</span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y" style={{ divideColor: T.border }}>
              {annotations.length === 0 && (
                <div className="p-6 text-center">
                  <Tag size={20} className="mx-auto mb-2" style={{ color: T.textMute }} />
                  <p className="text-xs" style={{ color: T.textSoft }}>
                    {L.noAnnotations}<br />
                    {L.noAnnotationsHint}
                  </p>
                </div>
              )}
              {annotations.map((ann, idx) => {
                const cfg = EVENT_MAP[ann.annotation_type] || { label: ann.annotation_type, color: '#999' };
                const timeS = (ann.timestamp_ms || 0) / 1000;
                const isNear = Math.abs(timeS - currentTime) < 0.5;
                return (
                  <div key={idx}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                    style={{ backgroundColor: isNear ? cfg.color + '08' : undefined }}
                    onClick={() => seekTo(timeS)}>
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
                      {ann.label && <span className="text-[10px] text-gray-400 ml-1">({PHASE_LABELS.find(p => p.key === ann.label)?.[lang === 'en' ? 'en' : 'pt'] || ann.label})</span>}
                      {ann.side && <span className="text-[10px] text-gray-400 ml-1">[{L.sideAbbr(ann.side)}]</span>}
                      <span className="text-[10px] ml-1.5 font-mono" style={{ color: T.textMute }}>
                        {fmtTime(ann.timestamp_ms)} · f{ann.frame_index || Math.round(timeS * fps)}
                      </span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); removeAnnotation(idx); }}
                      className="p-1 rounded hover:bg-red-50 shrink-0">
                      <Trash2 size={12} style={{ color: T.error }} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
